/* eslint-disable write-good-comments/write-good-comments -- rule names contain "only" which triggers false positives in oxlint directives */
// components/tutorial/tutorial-interactive-shell.tsx
// Client component for tutorial interactivity
// Handles: progress tracking, TOC, phase transitions, slideshow
/* oxlint-disable react-doctor/no-giant-component -- split deferred — see PR follow-up */
/* oxlint-disable react-doctor/prefer-use-effect-event -- refactor deferred */
/* oxlint-disable react-doctor/rerender-state-only-in-handlers -- state read in slideshow phase branch; refactor deferred */
/* oxlint-disable react-doctor/no-cascading-set-state -- state grouping deferred */
"use client";

import { memo, useCallback, useEffect, useState } from "react";

import { Button, cn, ProfileSection, ShareSection } from "@vllnt/ui";
import { Check, Play, RotateCcw } from "lucide-react";
import dynamic from "next/dynamic";

import { useTutorialAnalytics } from "@/hooks/use-tutorial-analytics";
import { useTutorialProgress } from "@/hooks/use-tutorial-progress";
import { Link } from "@/i18n/routing";
import { PRIMARY_SOCIAL_LINKS } from "@/lib/social";
import type { ExtractedSection, TutorialMeta } from "@/types/tutorial";

import { TutorialProgress } from "./tutorial-progress";

// Lazy-load slideshow - loaded when user clicks "Start Tutorial"
const TutorialSlideshow = dynamic(
  () =>
    import("./tutorial-slideshow").then((module_) => module_.TutorialSlideshow),
  {
    loading: () => (
      <div className="fixed inset-0 z-50 bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    ),
  },
);

type TutorialPhase = "intro" | "slideshow";

export type TutorialInteractiveShellProps = {
  contentId: string;
  dict: {
    profileName: string;
    profileTagline: string;
    progress: string;
    recommendedTutorials: string;
    sections: string;
    shareOn: string;
    shareTitle: string;
    tableOfContents: string;
  };
  estimatedTime: string;
  introSection: ExtractedSection;
  relatedTutorials?: TutorialMeta[];
  renderedIntro: React.ReactNode;
  renderedSections: React.ReactNode[];
  sections: ExtractedSection[];
  shareUrl: string;
  title: string;
  tutorialVersion: number;
};

// eslint-disable-next-line max-lines-per-function
function TutorialInteractiveShellImpl({
  contentId,
  dict,
  estimatedTime,
  introSection,
  relatedTutorials,
  renderedIntro,
  renderedSections,
  sections,
  shareUrl,
  title,
  tutorialVersion,
}: TutorialInteractiveShellProps): React.ReactNode {
  // Content sections (excluding intro)
  const contentSectionIds = sections.map((s) => s.id);

  // Progress hook - content renders at once, skeleton shown for progress bar
  const {
    completedSections,
    completionCount,
    isComplete,
    isLoading,
    resetProgress,
    toggleSection,
    totalSections,
  } = useTutorialProgress({
    contentId,
    sectionIds: contentSectionIds,
    tutorialVersion,
  });

  // Analytics hook
  const analytics = useTutorialAnalytics({
    totalSections,
    tutorialId: contentId,
    tutorialTitle: title,
  });

  // Phase state
  const [phase, setPhase] = useState<TutorialPhase>("intro");
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [hasHydrated, setHasHydrated] = useState(false);

  // Hydrate phase from URL hash
  useEffect(() => {
    if (hasHydrated) return;
    setHasHydrated(true);

    const hash = window.location.hash.slice(1);
    if (hash) {
      const index = sections.findIndex((s) => s.id === hash);
      if (index !== -1) {
        setCurrentSectionIndex(index);
        setPhase("slideshow");
      }
    }
  }, [hasHydrated, sections]);

  // URL hash updates
  const updateHash = useCallback(
    (index: null | number) => {
      if (index !== null && sections[index]) {
        const sectionId = sections[index].id;
        window.history.replaceState(null, "", `#${sectionId}`);
      } else {
        window.history.replaceState(null, "", window.location.pathname);
      }
    },
    [sections],
  );

  // Handlers
  const handleStart = useCallback(() => {
    analytics.trackStart();
    setCurrentSectionIndex(0);
    setPhase("slideshow");
    updateHash(0);
  }, [updateHash, analytics]);

  const handleExit = useCallback(() => {
    setPhase("intro");
    updateHash(null);
  }, [updateHash]);

  const handleComplete = useCallback(() => {
    const completionPercent =
      totalSections > 0
        ? Math.round((completedSections.size / totalSections) * 100)
        : 0;
    analytics.trackComplete(completionPercent);
    setPhase("intro");
    updateHash(null);
  }, [updateHash, analytics, completedSections.size, totalSections]);

  const handleRestart = useCallback(() => {
    resetProgress();
    setCurrentSectionIndex(0);
    updateHash(null);
  }, [resetProgress, updateHash]);

  const handleNavigate = useCallback(
    (index: number) => {
      setCurrentSectionIndex(index);
      updateHash(index);
    },
    [updateHash],
  );

  const handleGoToSection = useCallback(
    (index: number) => {
      const section = sections[index];
      if (section) {
        analytics.trackNavigation(section.id, "toc");
      }
      setCurrentSectionIndex(index);
      setPhase("slideshow");
      updateHash(index);
    },
    [updateHash, sections, analytics],
  );

  // Enter key to start
  useEffect(() => {
    if (phase !== "intro") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleStart();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [phase, handleStart]);

  const hasProgress = completedSections.size > 0;

  // Slideshow phase
  if (phase === "slideshow") {
    return (
      <TutorialSlideshow
        analytics={analytics}
        completedSections={completedSections}
        currentIndex={currentSectionIndex}
        onComplete={handleComplete}
        onExit={handleExit}
        onNavigate={handleNavigate}
        onToggleSection={toggleSection}
        renderedSections={renderedSections}
        sections={sections}
        title={title}
        totalSections={totalSections}
      />
    );
  }

  // Intro phase - render interactive MDX and controls
  return (
    <>
      {/* Progress Bar */}
      <TutorialProgress
        completionCount={completionCount}
        isComplete={isComplete}
        isLoading={isLoading}
        label={dict.progress}
        sectionsLabel={dict.sections}
        totalSections={totalSections}
      />

      <div className="mt-8 pb-24">
        {/* Interactive MDX Content with components */}
        <section className="py-6">
          <h2 className="text-2xl md:text-3xl font-semibold mb-6">
            {introSection.title}
          </h2>
          <div className="max-w-none min-h-[400px] [&_h2:first-of-type]:hidden">
            {renderedIntro}
          </div>
        </section>

        {/* Table of Contents */}
        <section className="mt-8 py-6 border-t border-border">
          <h3 className="text-lg font-semibold mb-4">{dict.tableOfContents}</h3>
          <ol className="space-y-2">
            {sections.map((section, index) => {
              const isCompleted =
                !isLoading && completedSections.has(section.id);
              return (
                // oxlint-disable-next-line react-doctor/no-array-index-as-key -- static section list, index is stable
                <li key={`${section.id}-${index}`}>
                  <button
                    className="w-full flex items-center gap-3 p-2 -m-2 rounded-lg hover:bg-muted/50 transition-colors text-left"
                    onClick={() => {
                      handleGoToSection(index);
                    }}
                    type="button"
                  >
                    <span
                      className={cn(
                        "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium tabular-nums transition-colors",
                        isLoading && "animate-pulse bg-muted",
                        !isLoading &&
                          isCompleted &&
                          "bg-foreground text-background",
                        !isLoading && !isCompleted && "bg-muted",
                      )}
                    >
                      {isCompleted ? <Check className="size-3" /> : index + 1}
                    </span>
                    <span
                      className={cn(
                        "text-sm",
                        isCompleted && "line-through text-muted-foreground",
                      )}
                    >
                      {section.title}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </section>

        {/* Share Section */}
        <div>
          <ShareSection
            shareOn={dict.shareOn}
            shareTitle={dict.shareTitle}
            title={title}
            url={shareUrl}
          />
        </div>

        {/* Recommended Tutorials */}
        {relatedTutorials && relatedTutorials.length > 0 ? (
          <div className="mt-8 pt-8 border-t border-border">
            <h3 className="text-lg font-semibold mb-4">
              {dict.recommendedTutorials}
            </h3>
            <div className="grid gap-2">
              {relatedTutorials.map((tutorial) => (
                <Link
                  className="group block p-4 rounded-lg hover:bg-muted/50 transition-colors overflow-hidden"
                  href={{
                    params: { slug: tutorial.slug },
                    pathname: "/tutorials/[slug]",
                  }}
                  key={tutorial.slug}
                >
                  <div className="flex items-start justify-between gap-4 min-w-0">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium group-hover:underline truncate">
                        {tutorial.title}
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2 break-words">
                        {tutorial.description}
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <span className="capitalize">
                          {tutorial.difficulty}
                        </span>
                        <span>·</span>
                        <span>{tutorial.estimatedTime}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : null}

        {/* Profile Section */}
        <div className="mt-8 pt-8 border-t border-border">
          <ProfileSection
            dict={{
              profile: {
                name: dict.profileName,
                tagline: dict.profileTagline,
              },
            }}
            imageSource="/profile.png"
            socialLinks={PRIMARY_SOCIAL_LINKS}
          />
        </div>
      </div>

      {/* Sticky CTA Bar - fixed height to prevent layout shift */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/80 backdrop-blur-sm safe-bottom">
        <div className="mx-auto max-w-3xl p-4 min-h-[76px] flex items-center">
          <div className="flex items-center justify-between gap-4 w-full">
            {/* Left side: status text */}
            <p className="text-sm text-muted-foreground hidden sm:block min-w-[120px]">
              {isLoading ? (
                <span className="inline-block h-5 w-28 bg-muted rounded animate-pulse" />
              ) : isComplete ? (
                <span className="flex items-center gap-2 text-green-600 dark:text-green-400 font-medium">
                  <Check className="size-4" />
                  Complete
                </span>
              ) : hasProgress ? (
                `${completedSections.size}/${sections.length} completed`
              ) : (
                `${sections.length} sections · ${estimatedTime}`
              )}
            </p>

            {/* Right side: action */}
            {!isLoading && isComplete ? (
              <button
                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 h-[52px]"
                onClick={handleRestart}
                type="button"
              >
                <RotateCcw className="size-3.5" />
                <span>Restart</span>
              </button>
            ) : (
              <Button
                className="flex-1 sm:flex-none px-8 py-6 text-lg font-medium gap-2 sm:min-w-[240px]"
                onClick={handleStart}
                size="lg"
              >
                <Play className="size-5 flex-shrink-0" />
                <span className="min-w-[130px] text-center">
                  {isLoading ? (
                    <span className="inline-block h-5 w-28 bg-primary-foreground/20 rounded animate-pulse" />
                  ) : hasProgress ? (
                    "Continue Tutorial"
                  ) : (
                    "Start Tutorial"
                  )}
                </span>
                <kbd className="hidden md:inline-flex ml-1 px-1.5 py-0.5 text-xs font-mono bg-primary-foreground/20 rounded flex-shrink-0">
                  ↵
                </kbd>
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export const TutorialInteractiveShell = memo(TutorialInteractiveShellImpl);
TutorialInteractiveShell.displayName = "TutorialInteractiveShell";
